import React, { useEffect, useRef, FC } from "react";
import { StyleSheet, View, ColorValue, Animated, Easing } from "react-native";
import { theme } from "ui-components";

type CircularProgressProps = {
  backgroundStrokeColor: ColorValue;
  strokeColor: ColorValue;
  durationMs?: number;
  accessibilityLabel?: string;
  testID?: string;
};

const startRotationAnimation = (durationMs: number, rotationDegree: Animated.Value): void => {
  Animated.loop(
    Animated.timing(rotationDegree, {
      toValue: 360,
      duration: durationMs,
      easing: Easing.linear,
      useNativeDriver: false,
    })
  ).start();
};

export const CircularProgress: FC<CircularProgressProps> = ({
  backgroundStrokeColor = theme.colors.text1,
  strokeColor = theme.colors.primary,
  durationMs = 1000,
  testID = "loading",
  accessibilityLabel = "loading",
}) => {
  //Using Animated from react-native since the npm package react-native-reanimated have a known issue
  //when working with expo sdk 49 https://github.com/expo/expo/issues/23297
  const rotationDegree = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    startRotationAnimation(durationMs, rotationDegree);
  }, [durationMs, rotationDegree]);

  return (
    <View style={styles.container}>
      <View style={[styles.background, { borderColor: "#FFFFFF" }]} />
      <Animated.View
        testID={testID + "-circular-progress"}
        accessibilityLabel={accessibilityLabel + " circular progress"}
        style={[
          styles.progress,
          {
            borderTopColor: strokeColor,
            borderLeftColor: backgroundStrokeColor,
            borderRightColor: backgroundStrokeColor,
            borderBottomColor: backgroundStrokeColor,
          },
          {
            transform: [
              {
                rotate: rotationDegree.interpolate({
                  inputRange: [0, 360],
                  outputRange: ["0deg", "360deg"],
                }),
              },
            ],
          },
        ]}
      />
    </View>
  );
};

const size = 60;
const styles = StyleSheet.create({
  container: {
    width: size,
    height: size,
    justifyContent: "center",
    alignItems: "center",
  },
  background: {
    width: "100%",
    height: "100%",
    borderRadius: size / 2,
    borderWidth: 6,
  },
  progress: {
    width: "100%",
    height: "100%",
    borderRadius: size / 2,
    borderWidth: 6,
    position: "absolute",
  },
});
